<template>
  <div class="locale-switcher" @keydown.esc="dropdownOpen = false">
    <button
      type="button"
      class="locale-selected"
      aria-haspopup="listbox"
      :aria-expanded="dropdownOpen"
      :aria-label="$t('language.label')"
      @click="toggleDropdown"
    >
      <img :src="localeFlags[currentLocale]" alt="" class="flag" />
      {{ currentLocale.toUpperCase() }}
    </button>

    <ul v-if="dropdownOpen" class="locale-options" role="listbox">
      <li v-for="availableLocale in availableLocales" :key="availableLocale" role="none">
        <button
          type="button"
          class="locale-option"
          role="option"
          :aria-selected="availableLocale === currentLocale"
          @click="setLocale(availableLocale)"
        >
          <img :src="localeFlags[availableLocale]" alt="" class="flag" />
          {{ availableLocale.toUpperCase() }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import enFlag from '@/assets/flags/en.svg';
import frFlag from '@/assets/flags/fr.svg';
import nlFlag from '@/assets/flags/nl.svg';

const { availableLocales, locale } = useI18n();
const dropdownOpen = ref(false);

const localeFlags = {
  en: enFlag,
  fr: frFlag,
  nl: nlFlag,
};

const currentLocale = computed(() => locale.value);

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};

const setLocale = (newLocale) => {
  locale.value = newLocale;
  dropdownOpen.value = false;
};

const syncDocumentLanguage = (newLocale) => {
  document.documentElement.lang = newLocale;
  try {
    window.localStorage.setItem('singing-locale', newLocale);
  } catch {
    // Language switching still works when storage is disabled.
  }
};

onMounted(() => syncDocumentLanguage(locale.value));
watch(locale, syncDocumentLanguage);
</script>

<style scoped>
.locale-switcher {
  position: relative;
  display: inline-block;
}

.locale-selected,
.locale-option {
  display: flex;
  width: 100%;
  align-items: center;
  border: 0;
  background: #fff;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.locale-selected {
  min-height: 40px;
  padding: 7px 12px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  font-size: 0.88rem;
  font-weight: 700;
  box-shadow: var(--shadow-sm);
}

.locale-options {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 10;
  width: 100%;
  min-width: 86px;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: white;
  list-style: none;
  box-shadow: var(--shadow-md);
}

.locale-option {
  padding: 7px 15px;
}

.locale-option:hover,
.locale-option:focus-visible {
  color: var(--primary-color);
  background: var(--warm-bg);
}

.flag {
  width: 20px;
  height: 14px;
  margin-right: 8px;
  object-fit: cover;
}
</style>
